import { useInRouterContext } from 'react-router-dom';

import { SceneReviewWorkspace } from '../../components/scenes/SceneReviewWorkspace.js';
import {
  LocalSceneAddress,
  RoutedSceneAddress,
  type SceneAddress,
  type SceneFilter,
  type SetSceneAddress,
} from '../../components/scenes/sceneAddress.js';

/**
 * STEP 2 — REVIEW EXTRACTED SCENES (owner brief, 2026-10-29; redesigned
 * 2026-10-30).
 *
 * One question, and only one: did the parser find the screenplay's real scenes,
 * with the right headings, in the right order? The screen is the scene-review
 * workspace the Script tab also mounts — a summary with one primary action, the
 * scene list beside the scene being read — framed as the guided path's step 2.
 *
 * EVERY CORRECTION IS SAVED WHEN IT IS MADE, so "Save & finish later" is only
 * leaving, and coming back finds everything as it was left.
 */

export type Filter = SceneFilter;

export function ReviewScenesStep({
  projectId,
  onContinue,
  onFinishLater,
  filter,
  query,
  onFilterChange,
  onQueryChange,
}: {
  projectId: string;
  /** Called once the scenes are confirmed — the choice of workflows comes next. */
  onContinue: () => void;
  /** Leave; everything is already saved. */
  onFinishLater: () => void;
  /**
   * The filter and search, when a caller owns them. Under a router the address
   * (`?filter=&q=&scene=`) is the owner and these are only its echo.
   */
  filter?: Filter;
  query?: string;
  onFilterChange?: (filter: Filter) => void;
  onQueryChange?: (query: string) => void;
}) {
  const routed = useInRouterContext();

  const render = (address: SceneAddress, set: SetSceneAddress) => (
    <div className="scenes-page" data-testid="review-scenes-step">
      <SceneReviewWorkspace projectId={projectId} mode="setup" address={address} onAddress={set} onContinue={onContinue} />

      <div className="sw-step-actions" data-testid="scenes-actions">
        <button type="button" className="btn btn-ghost" data-testid="scenes-finish-later" onClick={onFinishLater}>
          Save &amp; finish later
        </button>
        <span className="mm sw-saved-note">Every change is saved as you make it.</span>
      </div>
    </div>
  );

  return routed ? (
    <RoutedSceneAddress>{render}</RoutedSceneAddress>
  ) : (
    <LocalSceneAddress filter={filter} query={query} onFilterChange={onFilterChange} onQueryChange={onQueryChange}>
      {render}
    </LocalSceneAddress>
  );
}
